import { useState } from "react";
import { Alert, Form, Input, Modal, Select } from "antd";

import { ApiError, api } from "../api/client";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (strichlisteUserId: number) => void;
}

interface FormValues {
  nickname: string;
  email?: string;
  real_name?: string;
  user_type: "regular" | "guest";
}

/**
 * Modal for creating a new user. ``user_type`` defaults to Regular when
 * an email is provided, Guest otherwise — matches the brief.
 */
export const AddUserModal = ({ open, onClose, onCreated }: Props) => {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const created = await api.createUser({
        name: values.nickname,
        email: values.email || undefined,
        real_name: values.real_name || undefined,
        user_type: values.user_type,
      });
      form.resetFields();
      onCreated(created.strichliste_user_id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Add user"
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Create"
      confirmLoading={submitting}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{ user_type: "regular" }}
        onValuesChange={(changed, all) => {
          if ("email" in changed) {
            form.setFieldsValue({
              user_type: all.email ? "regular" : "guest",
            });
          }
        }}
      >
        <Form.Item
          name="nickname"
          label="Nickname"
          rules={[{ required: true, max: 64 }]}
        >
          <Input autoFocus />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ type: "email", message: "Invalid email" }]}
        >
          <Input placeholder="optional" />
        </Form.Item>
        <Form.Item name="real_name" label="Real name">
          <Input placeholder="optional" />
        </Form.Item>
        <Form.Item name="user_type" label="Type">
          <Select
            options={[
              { value: "regular", label: "Regular" },
              { value: "guest", label: "Guest" },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
